import React from 'react';
import { motion } from 'framer-motion';
import { SparkleIcon, ChevronDownIcon } from 'lucide-react';
import { BookMockup } from '../BookMockup';
import { StarField } from '../StarField';
import { SacredBackground } from '../SacredBackground';
import { CTAButton } from '../CTAButton';
import { SHOPIFY_URL } from '../../constants';
const floatingWords = [{
  text: 'Equilibrio',
  x: '6%',
  y: '12%',
  delay: 0,
  scale: 1
}, {
  text: 'Vitalidad',
  x: '80%',
  y: '6%',
  delay: 0.6,
  scale: 0.95
}, {
  text: 'Intuición',
  x: '2%',
  y: '60%',
  delay: 1.2,
  scale: 1.05
}, {
  text: 'Propósito',
  x: '84%',
  y: '72%',
  delay: 1.8,
  scale: 1
}, {
  text: 'Disciplina',
  x: '74%',
  y: '36%',
  delay: 2.4,
  scale: 0.9
}, {
  text: 'Armonía',
  x: '8%',
  y: '88%',
  delay: 3,
  scale: 1.05
}];
const driftParticles = [{
  x: '20%',
  y: '30%',
  size: 4,
  delay: 0
}, {
  x: '85%',
  y: '25%',
  size: 3,
  delay: 1
}, {
  x: '15%',
  y: '75%',
  size: 5,
  delay: 2
}, {
  x: '78%',
  y: '80%',
  size: 3,
  delay: 3
}];
export const Hero: React.FC = () => {
  return <section className="relative min-h-screen flex items-center overflow-hidden bg-navy-deep vignette" data-id="element-183">
      {/* Layered cosmic backgrounds */}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(ellipse at 75% 15%, rgba(168,197,224,0.15), transparent 45%),' + 'radial-gradient(ellipse at 15% 85%, rgba(107,91,149,0.22), transparent 50%),' + 'radial-gradient(ellipse at 70% 60%, rgba(212,175,122,0.18), transparent 50%),' + 'radial-gradient(ellipse at 50% 100%, rgba(168,197,224,0.08), transparent 60%)'
    }} data-id="element-184" />

      {/* Aurora SVG sweep */}
      <div className="absolute inset-0 pointer-events-none aurora" data-id="element-185">
        <svg className="w-full h-full" viewBox="0 0 1440 900" preserveAspectRatio="none" data-id="element-186">
          <defs data-id="element-187">
            <linearGradient id="auroraGrad" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-188">
              <stop offset="0%" stopColor="#A8C5E0" stopOpacity="0" data-id="element-189" />
              <stop offset="40%" stopColor="#D4AF7A" stopOpacity="0.18" data-id="element-190" />
              <stop offset="70%" stopColor="#6B5B95" stopOpacity="0.12" data-id="element-191" />
              <stop offset="100%" stopColor="#0A1128" stopOpacity="0" data-id="element-192" />
            </linearGradient>
          </defs>
          <path d="M -200 400 Q 400 200, 800 350 T 1700 250 L 1700 900 L -200 900 Z" fill="url(#auroraGrad)" data-id="element-193" />
        </svg>
      </div>

      <SacredBackground data-id="element-194" />
      <StarField count={90} data-id="element-195" />
      <div className="absolute inset-0 sacred-grid opacity-25" data-id="element-196" />

      {/* Top nav */}
      <header className="absolute top-0 left-0 right-0 z-30 px-6 lg:px-12 py-7 flex justify-between items-center" data-id="element-197">
        <div className="flex items-center gap-2.5" data-id="element-198">
          <SparkleIcon className="w-4 h-4 text-gold" strokeWidth={1.5} data-id="element-199" />
          <span className="font-serif italic text-ivory/85 text-sm tracking-wide" data-id="element-200">
            Daniel Navarro
          </span>
        </div>
        <a href={SHOPIFY_URL} className="text-[11px] uppercase tracking-eyebrow text-ivory/55 hover:text-gold transition-colors" data-id="element-201">
          Comprar →
        </a>
      </header>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-32 lg:py-24 grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-16 items-center w-full" data-id="element-202">
        {/* Left */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1.4,
        ease: [0.22, 1, 0.36, 1]
      }} className="order-2 lg:order-1 text-center lg:text-left" data-id="element-203">
          {/* Thin gold rule above eyebrow */}
          <div className="mb-5 flex justify-center lg:justify-start" data-id="element-204">
            <span className="block w-16 h-px bg-gradient-to-r from-gold to-transparent" data-id="element-205" />
          </div>

          <div className="flex items-center justify-center lg:justify-start gap-3 mb-9" data-id="element-206">
            <span className="text-[10px] uppercase tracking-eyebrow text-gold font-medium" data-id="element-207">
              Un viaje hacia tu equilibrio interior
            </span>
          </div>

          <h1 className="font-serif text-[44px] sm:text-6xl lg:text-7xl xl:text-[88px] leading-[1.02] text-balance text-ivory mb-9" style={{
          fontWeight: 300,
          letterSpacing: '-0.015em'
        }} data-id="element-208">
            La estabilidad que buscas
            <span className="italic gold-text" style={{
            fontWeight: 400
          }} data-id="element-209">
              {' '}
              no está afuera.
            </span>
            <br data-id="element-210" />
            <span style={{
            fontWeight: 400
          }} data-id="element-211">
              Empieza dentro de ti.
            </span>
          </h1>

          <p className="text-base lg:text-lg text-ivory/70 leading-[1.8] max-w-xl mb-11 font-light mx-auto lg:mx-0" data-id="element-212">
            Descubre las diez claves para armonizar tu cuerpo, tu mente, tus
            emociones, tu intuición y tu propósito en una vida más plena,
            consciente y coherente.
          </p>

          <div className="flex flex-col sm:flex-row gap-5 sm:gap-6 items-center sm:items-center justify-center lg:justify-start" data-id="element-213">
            <CTAButton size="lg" pulse trustLine={null} data-id="element-214">
              Obtener el libro ahora
            </CTAButton>
            <a href="#claves" className="text-ivory/70 text-sm tracking-wide hover:text-gold transition-colors group" data-id="element-215">
              <span className="inline-block border-b border-gold/30 group-hover:border-gold pb-0.5" data-id="element-216">
                Ver lo que hay dentro
              </span>
            </a>
          </div>

          <p className="text-[10px] text-ivory/45 tracking-[0.18em] uppercase mt-6 leading-relaxed text-center lg:text-left" data-id="element-217">
            <span className="block sm:inline" data-id="element-218">Acceso instantáneo · PDF</span>
            <span className="hidden sm:inline" data-id="element-219"> · </span>
            <span className="block sm:inline" data-id="element-220">Pago seguro vía Shopify</span>
          </p>
        </motion.div>

        {/* Right - book */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.92
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 1.6,
        delay: 0.4,
        ease: [0.22, 1, 0.36, 1]
      }} className="order-1 lg:order-2 relative flex justify-center items-center min-h-[480px] lg:min-h-[660px]" data-id="element-221">
          {/* Drifting particles */}
          {driftParticles.map((p, i) => <motion.div key={i} className="absolute rounded-full pointer-events-none" style={{
          left: p.x,
          top: p.y,
          width: p.size,
          height: p.size,
          background: '#D4AF7A',
          boxShadow: '0 0 14px rgba(212,175,122,0.9)'
        }} animate={{
          y: [0, -30, 0],
          x: [0, 15, 0],
          opacity: [0.3, 0.9, 0.3]
        }} transition={{
          duration: 8 + i,
          delay: p.delay,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-222" />)}

          {/* Floating words */}
          {floatingWords.map(w => <motion.span key={w.text} className="absolute font-serif italic text-gold/65 text-[11px] sm:text-sm md:text-base pointer-events-none whitespace-nowrap" style={{
          left: w.x,
          top: w.y
        }} initial={{
          opacity: 0,
          y: 10,
          scale: w.scale
        }} animate={{
          opacity: [0, 0.7, 0.35, 0.65, 0.4],
          y: [10, 0, -6, 2, 0],
          scale: [w.scale, w.scale * 1.05, w.scale * 0.98, w.scale]
        }} transition={{
          duration: 10,
          delay: w.delay,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-223">
              · {w.text}
            </motion.span>)}

          <BookMockup size="xl" data-id="element-224" />
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 2,
      duration: 1.5
    }} className="absolute bottom-8 left-0 right-0 z-20 flex flex-col items-center gap-2 scroll-hint" data-id="element-225">
        <span className="text-[10px] uppercase tracking-eyebrow text-gold/70" data-id="element-226">
          Descubre
        </span>
        <div className="w-[1.5px] h-7 bg-gradient-to-b from-gold/60 to-transparent rounded-full" data-id="element-227" />
        <ChevronDownIcon className="w-3.5 h-3.5 text-gold/60" strokeWidth={1.5} data-id="element-228" />
      </motion.div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-navy-deep via-navy-deep/80 to-transparent pointer-events-none" data-id="element-229" />
    </section>;
};